import { z } from 'zod';
import { toIssues } from '../materials/catalog.js';
import type { ParseResult } from '../voyage/schemas.js';
import type { DrillRating, DrillStepResult } from './execution.js';
import type { DrillCriterion } from './template.js';

/**
 * Tatbikat bulguları ve düzeltici faaliyetler (ortağın belgesi §7, §9; ADR 0128 Faz 4 Ek). Bulgu
 * gemi verisidir; dört türden biridir ve **mevcut bir modülde gerçek bir kayıt açar** (ikinci yazma
 * yolu yoktur): Equipment Defect → arıza (LGS-17; ADR 0060, 0096), SMS Non-Conformity → HSEQ
 * raporu (ADR 0097), Work Order → PMS iş emri, Training Need → eğitim ihtiyacı (Crew ▸ Training).
 * Bağ iki yönlüdür: bulgu bağlı kaydı, bağlı kayıt kaynağını ("Source: Drill DR-…") gösterir.
 *
 * Uygunsuzluk **kendiliğinden ve zorunlu açılmaz** (belge §7): öneriler NC türünü hiç önermez;
 * bulguyu NC olarak sınıflamak yetkili kişinin (kaptan) kararıdır. Saf modül.
 */

export const DRILL_FINDING_TYPES = [
  'equipment_defect',
  'sms_nc',
  'work_order',
  'training_need',
] as const;
export type DrillFindingType = (typeof DRILL_FINDING_TYPES)[number];

/** Bulgunun bağlı kaydı (bulgu satırındaki kolon). Bağ bir kez kurulur. */
export const DRILL_FINDING_LINK = {
  equipment_defect: 'defect_id',
  sms_nc: 'hse_report_id',
  work_order: 'work_order_id',
  training_need: 'training_need_id',
} as const satisfies Record<DrillFindingType, string>;
export type DrillFindingLinkColumn = (typeof DRILL_FINDING_LINK)[DrillFindingType];

/**
 * Bulgunun kaynağı: hazırlıkta ekipman hazır değil (`preparation`), tamamlanmayan adım (`step`,
 * anahtar adım sırası), Needs Improvement kriteri (`evaluation`, anahtar kriter), kişinin Needs
 * Improvement değerlendirmesi (`crew`, anahtar kişi) ya da elle (`manual`).
 */
export const DRILL_FINDING_SOURCES = [
  'preparation',
  'step',
  'evaluation',
  'crew',
  'manual',
] as const;
export type DrillFindingSource = (typeof DRILL_FINDING_SOURCES)[number];

/** Bulgunun numarası: kaydın numarası + sıra (`DR-2026-004-F1`). */
export function drillFindingNumber(recordNo: string, lastSeq: number) {
  const seq = lastSeq + 1;
  return { seq, number: `${recordNo}-F${seq}` };
}

/* ---------- Şema ---------- */

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');
const paragraph = (value: string) =>
  value
    .normalize('NFC')
    .split(/\r\n|\r|\n/)
    .map((line) => line.trim().replace(/[ \t]+/g, ' '))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
const requiredParagraph = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(paragraph)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));
const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);
const nullableUuid = z
  .uuid('INVALID_ID')
  .nullish()
  .transform((value) => value ?? null);
const DAY = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const day = z
  .string(typed('INVALID_DATE'))
  .regex(DAY, 'INVALID_DATE')
  .refine((value) => {
    const date = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
  }, 'INVALID_DATE')
  .nullish()
  .transform((value) => value ?? null);

function parsed<T>(result: z.ZodSafeParseResult<T>): ParseResult<T> {
  return result.success
    ? { ok: true, value: result.data }
    : {
        ok: false,
        issues: toIssues(result.error).filter(
          (issue, index, all) => all.findIndex((i) => i.path === issue.path) === index,
        ),
      };
}

export const drillFindingSchema = z
  .strictObject({
    type: z.enum(DRILL_FINDING_TYPES, typed('INVALID_OPTION')),
    source: z.enum(DRILL_FINDING_SOURCES, typed('INVALID_OPTION')),
    sourceKey: optionalText(80),
    description: requiredParagraph(2000),
    /** Sorumlu (gemideki kişi) ve hedef tarih (belge §7: sorumlu, hedef tarih izlenir). */
    responsibleUserId: nullableUuid,
    dueOn: day,
    /** "Gerekirse tekrar tatbikat" (belge §7): bağlı kayıt kapanınca Planner'a öneri düşer. */
    redrill: z
      .boolean(typed('INVALID_BOOLEAN'))
      .nullish()
      .transform((value) => value ?? false),
    /** Eğitim ihtiyacının kişisi (Training Need'de zorunlu). */
    personUserId: nullableUuid,
  })
  .superRefine((value, ctx) => {
    if (value.type === 'training_need' && value.personUserId === null) {
      ctx.addIssue({ code: 'custom', path: ['personUserId'], message: 'REQUIRED' });
    }
    if (value.source !== 'manual' && value.sourceKey === null) {
      ctx.addIssue({ code: 'custom', path: ['sourceKey'], message: 'REQUIRED' });
    }
  });
export type DrillFindingInput = z.output<typeof drillFindingSchema>;

export function parseDrillFinding(input: unknown): ParseResult<DrillFindingInput> {
  return parsed(drillFindingSchema.safeParse(input));
}

export const drillFindingCancelSchema = z.strictObject({ reason: requiredParagraph(2000) });

export function parseDrillFindingCancel(input: unknown): ParseResult<{ reason: string }> {
  return parsed(drillFindingCancelSchema.safeParse(input));
}

/**
 * Ofisin bulgu değerlendirmesi (belge §10: Superintendent / DPA "filo takibi, bulgu
 * değerlendirmesi"): düzeltici faaliyet yeterli ya da ek faaliyet gerekli; yorum ek faaliyette
 * zorunlu. Değerlendirme gemi verisine yazmaz, kaptan bölümlerini değiştirmez.
 */
export const DRILL_FINDING_ASSESSMENTS = ['adequate', 'further_action'] as const;
export type DrillFindingAssessment = (typeof DRILL_FINDING_ASSESSMENTS)[number];

export const drillFindingReviewSchema = z
  .strictObject({
    assessment: z.enum(DRILL_FINDING_ASSESSMENTS, typed('INVALID_OPTION')),
    comment: z
      .string(typed('INVALID_TEXT'))
      .transform(paragraph)
      .pipe(z.string().max(2000, 'TOO_LONG'))
      .transform((value) => (value === '' ? null : value))
      .nullish()
      .transform((value) => value ?? null),
  })
  .superRefine((value, ctx) => {
    if (value.assessment === 'further_action' && value.comment === null) {
      ctx.addIssue({ code: 'custom', path: ['comment'], message: 'REQUIRED' });
    }
  });
export type DrillFindingReviewInput = z.output<typeof drillFindingReviewSchema>;

export function parseDrillFindingReview(input: unknown): ParseResult<DrillFindingReviewInput> {
  return parsed(drillFindingReviewSchema.safeParse(input));
}

/* ---------- Öneriler ---------- */

export interface DrillFindingSuggestion {
  source: Exclude<DrillFindingSource, 'manual'>;
  sourceKey: string;
  /** Tür ipucu; `null`: kişi seçer. Uygunsuzluk (`sms_nc`) hiçbir zaman önerilmez (belge §7). */
  type: Exclude<DrillFindingType, 'sms_nc'> | null;
  description: string;
  personUserId: string | null;
}

const withNote = (head: string, note: string | null | undefined) =>
  note ? `${head}: ${note}` : head;

/**
 * Bulgu önerileri (belge §7; ADR 0128 Faz 4 Ek): hazırlıkta "ekipman hazır değil", tamamlanmayan
 * adım, Needs Improvement kriteri ve kişinin Needs Improvement değerlendirmesi. Ekipman kaynaklı
 * öneri arıza, kişi kaynaklı öneri eğitim ihtiyacı türünü önerir; ötekilerde tür kişinindir.
 * Öneri yazılmaz; açılmış (iptal edilmemiş) bulgunun kaynağı yeniden önerilmez.
 */
export function suggestDrillFindings(input: {
  criteria: readonly DrillCriterion[];
  evaluation: {
    results: readonly { key: string; rating: DrillRating | null; note?: string | null }[];
    crew?: readonly { userId: string; rating: DrillRating | null; note: string | null }[];
  };
  steps: readonly {
    stepNo: number;
    text: string;
    result: DrillStepResult | null;
    remarks: string | null;
  }[];
  preparation: { equipmentReady: boolean | null; equipmentNotes: string | null };
  participants: readonly { userId: string | null; name: string }[];
  existing: readonly { source: string; sourceKey: string | null }[];
}): DrillFindingSuggestion[] {
  const taken = new Set(input.existing.map((e) => `${e.source}:${e.sourceKey ?? ''}`));
  const out: DrillFindingSuggestion[] = [];
  const push = (suggestion: DrillFindingSuggestion) => {
    if (!taken.has(`${suggestion.source}:${suggestion.sourceKey}`)) out.push(suggestion);
  };
  if (input.preparation.equipmentReady === false) {
    push({
      source: 'preparation',
      sourceKey: 'equipmentReady',
      type: 'equipment_defect',
      description: withNote('Equipment not ready', input.preparation.equipmentNotes),
      personUserId: null,
    });
  }
  for (const step of input.steps) {
    if (step.result !== 'not_completed') continue;
    push({
      source: 'step',
      sourceKey: String(step.stepNo),
      type: null,
      description: withNote(`Step ${step.stepNo} not completed — ${step.text}`, step.remarks),
      personUserId: null,
    });
  }
  for (const criterion of input.criteria) {
    if (criterion.kind !== 'rating') continue;
    const result = input.evaluation.results.find((r) => r.key === criterion.key);
    if (result?.rating !== 'needs_improvement') continue;
    push({
      source: 'evaluation',
      sourceKey: criterion.key,
      type: criterion.key === 'equipment_readiness' ? 'equipment_defect' : null,
      description: withNote(`${criterion.label} — Needs Improvement`, result.note),
      personUserId: null,
    });
  }
  for (const person of input.evaluation.crew ?? []) {
    if (person.rating !== 'needs_improvement') continue;
    const name = input.participants.find((p) => p.userId === person.userId)?.name;
    if (!name) continue;
    push({
      source: 'crew',
      sourceKey: person.userId,
      type: 'training_need',
      description: withNote(`${name} — Needs Improvement`, person.note),
      personUserId: person.userId,
    });
  }
  return out;
}

/* ---------- Durum ---------- */

/** Bağlı kaydın durumu: bağ yok, açık, kapalı (arıza kapanışı onaylı, NC kapalı …). */
export type DrillFindingLinkState = 'none' | 'open' | 'closed';
export const DRILL_FINDING_STATUSES = ['open', 'in_progress', 'closed', 'cancelled'] as const;
export type DrillFindingStatus = (typeof DRILL_FINDING_STATUSES)[number];

/**
 * Bulgunun durumu saklanmaz (ADR 0128 Faz 3 kararı 2 kalıbı): iptal edilmişse Cancelled; bağlı
 * kayıt yoksa Open; bağlı kayıt açıksa In Progress; bağlı kayıt kendi modülünde doğrulanıp
 * kapandıysa Closed. Doğrulama ve kapanış bağlı kaydın modülündedir (arızada kaptanın onarım
 * doğrulaması, NC'de doğrulama ve kapanış, iş emrinde rapor, eğitimde tamamlanma).
 */
export function drillFindingStatus(input: {
  cancelled: boolean;
  link: DrillFindingLinkState;
}): DrillFindingStatus {
  if (input.cancelled) return 'cancelled';
  if (input.link === 'none') return 'open';
  return input.link === 'open' ? 'in_progress' : 'closed';
}

/** Hedef tarihi (gemi günü) geçmiş ve kapanmamış bulgu. */
export function drillFindingOverdue(
  status: DrillFindingStatus,
  dueOn: string | null,
  today: string,
): boolean {
  return (status === 'open' || status === 'in_progress') && dueOn !== null && dueOn < today;
}
